import { OrderHoldSubtype, OrderHoldType } from '@invenco/common-domain/enums';
import {
  OrderLineStatus,
  type OrderDTO,
  type OrderHoldDTO,
} from '@invenco/sales-interface/interfaces';

const HOLD_TITLES: Partial<Record<OrderHoldType, string>> = {
  [OrderHoldType.AUTOMATION_PROCESS]: 'Automation failed',
  [OrderHoldType.RESERVE_INVENTORY_PROCESS]: 'Placed on backorder',
  [OrderHoldType.SHIPPING_ADDRESS_PROCESS]: 'Address validation failed',
  [OrderHoldType.ITEM_NOT_FOUND]: 'Item not found',
  [OrderHoldType.SHIPPING_HOLD]: 'Shipping hold',
};

function getBackorderDescription(order: OrderDTO) {
  const backorderLines =
    order.orderLines?.filter((line) => line.status === OrderLineStatus.BACKORDER) ?? [];

  if (backorderLines.length === 0) return 'Unable to fully reserve inventory';
  if (backorderLines.length > 1) {
    return `${backorderLines.length} items on backorder`;
  }

  return `${backorderLines[0].skuName} is on backorder`;
}

function getInvalidSkuDescription(order: OrderDTO) {
  const invalidLines =
    order.orderLines?.filter((line) => line.status === OrderLineStatus.ITEM_NOT_FOUND) ?? [];

  if (invalidLines.length === 0) return 'One or more SKUs are invalid';
  if (invalidLines.length > 1) {
    return `${invalidLines.length} invalid SKU names`;
  }

  return `${invalidLines[0].skuName} is an invalid SKU name`;
}

export function getHoldDescription(order: OrderDTO, hold: OrderHoldDTO) {
  if (hold.description) return hold.description;

  if (hold.subtype) {
    switch (hold.subtype) {
      case OrderHoldSubtype.NO_PREFERRED_RATE:
        return `Cannot determine a preferred carrier service for shipping profile ${order.shippingProfileName}`;
      case OrderHoldSubtype.SHIPPING_PROFILE_NOT_FOUND:
        return `Unable to find a shipping profile${
          order.shippingDescription ? ` for description "${order.shippingDescription}"` : ''
        }`;
      case OrderHoldSubtype.MISSING_CUSTOMS_DATA:
        return 'Missing required customs data for international shipping';
    }
  }

  switch (hold.type) {
    case OrderHoldType.SHIPPING_ADDRESS_PROCESS:
      return 'Invalid customer address';
    case OrderHoldType.RESERVE_INVENTORY_PROCESS:
      return getBackorderDescription(order);
    case OrderHoldType.AUTOMATION_PROCESS:
      return 'Automation process failed';
    case OrderHoldType.ITEM_NOT_FOUND:
      return getInvalidSkuDescription(order);
    case OrderHoldType.MANUAL_HOLD:
      return 'Manually placed on hold';
    case OrderHoldType.SHIPPING_HOLD:
      return 'Error in shipping';
  }

  return undefined;
}

export function getHoldTitle(hold: OrderHoldDTO, action: 'applied' | 'released') {
  if (hold.type === OrderHoldType.MANUAL_HOLD) return `Manual hold ${action}`;

  return HOLD_TITLES[hold.type] ?? hold.type;
}
